import { IncomingMessage } from 'http'
import _ from 'lodash'
import React from 'react'
import Parser, { domToReact } from 'html-react-parser'
import { connect, ConnectedProps } from 'react-redux'

import { AppState } from 'common/store/reducer'
import { requestHtmlContent } from 'common/services/html-content/actions'
import actionLinks from 'common/action-links'
import { replaceMediaLinksInContent } from 'common/utils'

import ContentLoader from './svg-text-loader'

const connector = connect(
  (state: AppState) => ({
    htmlContentStore: state.htmlContent,
  }),
  (dispatch) => ({
    requestHtmlContent: (payload: { req?: IncomingMessage; keys: string[] }) =>
      dispatch(requestHtmlContent(payload)),
  }),
)

type Props = ConnectedProps<typeof connector> & {
  slug: string
  urlParams?: Record<string, unknown>
  replaceLinkContent?: (opts: {
    attribs: Record<string, string>
    contents: React.ReactNode
  }) => React.ReactNode
  hideLoader?: boolean
  skipEmpty?: boolean
  skipParagraphs?: boolean
}

class ExternalHtml extends React.Component<Props> {
  static defaultProps = {
    hideLoader: false,
    skipEmpty: false,
    skipParagraphs: false,
    replaceLinkContent: undefined,
  }

  componentDidMount(): void {
    const { slug } = this.props
    this.props.requestHtmlContent({ keys: [slug] })
  }

  get pureHtml(): string {
    return replaceMediaLinksInContent(_.get(this.props.htmlContentStore, this.props.slug) || '')
  }

  renderNoParagraphs(): React.ReactNode {
    return Parser(this.pureHtml, {
      replace: (domNode) => {
        const node = domNode as { name?: string; children?: unknown[] }
        if (node.name === 'p') {
          return (
            <>{domToReact((node.children ?? []) as Parameters<typeof domToReact>[0])}</>
          ) as React.ReactElement
        }
      },
    })
  }

  renderCustomLinks(): React.ReactNode {
    const { replaceLinkContent } = this.props
    if (!replaceLinkContent) return null
    return Parser(this.pureHtml, {
      replace: (domNode) => {
        const node = domNode as {
          name?: string
          attribs?: Record<string, string>
          children?: unknown[]
        }
        if (node.name === 'a') {
          return replaceLinkContent({
            attribs: node.attribs ?? {},
            contents: domToReact((node.children ?? []) as Parameters<typeof domToReact>[0]),
          }) as React.ReactElement
        }
      },
    })
  }

  renderActionLinks(): React.ReactNode {
    const { urlParams = {} } = this.props
    return Parser(this.pureHtml, {
      replace: (domNode) => {
        const node = domNode as { name?: string; attribs?: { href?: string }; children?: unknown[] }
        if (node.name === 'a') {
          const link = _.find(actionLinks, { action: node.attribs?.href })
          const url = link?.url?.(urlParams)
          return (
            url != null ? (
              <a href={url}>
                {domToReact((node.children ?? []) as Parameters<typeof domToReact>[0])}
              </a>
            ) : undefined
          ) as React.ReactElement | undefined
        }
      },
    })
  }

  renderContents(): React.ReactNode {
    if (this.props.skipParagraphs) {
      return this.renderNoParagraphs()
    }
    if (this.props.replaceLinkContent) {
      return this.renderCustomLinks()
    }
    return this.renderActionLinks()
  }

  render(): React.ReactNode {
    const { slug, hideLoader, skipEmpty } = this.props
    if (!this.pureHtml) {
      if (skipEmpty) {
        return null
      }
      return <data value={slug}>{!hideLoader ? <ContentLoader /> : null}</data>
    }
    return <data value={slug}>{this.renderContents()}</data>
  }
}

export default connector(ExternalHtml)
